/**
 * CZNA-1034 — the heading's `$a` turned into a namesake query.
 *
 * <p>The existing duplicate check (`DUPLICATE_HEADING`, CZNA-221) matches the
 * FULLY COMPOSED heading — name plus life dates plus qualifiers. Measured on the
 * live corpus 7. 9. 2026: a candidate of `100 $a Ondroušek, Antonín,` produced
 * zero duplicate warnings while two such authorities exist, because the stored
 * headings are `Ondroušek, Antonín, 1863-1939` and `Ondroušek, Antonín, 1930-`.
 * That is correct — two people of the same name with different dates are not
 * duplicates — but it answers "is this the same heading?", not "is this person
 * already in the base?", and those diverge exactly where it matters: a record
 * whose dates are uncertain ("asi 1350-asi 1420") can never collide on the
 * composed form.
 *
 * <p>So the hint queries the NAME, as a prefix. Because the composed heading
 * BEGINS with the name, a prefix query over heading text returns precisely the
 * namesakes, with no new endpoint: `fetchRecords({ query, searchMode:
 * 'STARTS_WITH', type })`.
 */
/**
 * Shortest query worth asking about.
 *
 * <p>The backend exempts `STARTS_WITH` from its own minimum-query guard
 * (`SearchQueryGuard`, CZNA-628) because a prefix is indexable at any length —
 * so the floor is ours to set, and it is about the reader, not the database. One
 * or two characters match tens of thousands of headings; nothing about that list
 * tells a cataloguer whether her person is in it.
 */
export var MIN_NAME_QUERY_LENGTH = 3;
/**
 * Mirror of `HeadingComposer.stripTrailingPunctuation` — Java
 * `value.replaceAll("[,;.\\s]+$", "")` — so the namesake query trims a name the way
 * the stored heading does.
 *
 * <p>The whitespace class is spelled out rather than written `\s`, because the
 * two languages disagree about what `\s` means: Java's is ASCII-only, JS's also
 * matches NBSP and the other Unicode spaces. Catalogue data pasted from Word
 * does carry NBSP, so `\s` here would strip a character Java keeps.
 *
 * <p>It lived in the heading-preview module until CZNA-1418 removed that module (the
 * preview is composed by the backend now); this query is its only remaining reader.
 */
export var stripTrailingPunctuation = function (value) { return value.replace(/[,;.\t\n\v\f\r ]+$/, ''); };
/**
 * Normalises a heading `$a` into a namesake query, or `null` when there is
 * nothing worth asking.
 *
 * <p>Trailing ISBD punctuation is dropped because the corpus stores the same
 * name both ways — `Novák, Jan` on 205 active records and `Novák, Jan,` on 42
 * (measured 7. 9. 2026). An exact-match query would split namesakes by
 * punctuation and miss the collision the hint exists to surface.
 *
 * <p>Trimming is safe only because the search is a PREFIX match: a shorter
 * prefix matches more rows, never fewer. That is also why nothing but trailing
 * separators is touched — trimming into the name itself could reach a different
 * name.
 *
 * <p>Diacritics are left alone. The search folds them itself (verified live
 * 7. 9. 2026: `Ondrousek, Antonin` returns the same two records as the accented
 * form), so stripping them here would only make the request harder to read.
 */
export var headingNameQuery = function (subfieldA) {
    if (!subfieldA)
        return null;
    var trimmed = stripTrailingPunctuation(subfieldA).trim();
    if (trimmed.length < MIN_NAME_QUERY_LENGTH)
        return null;
    return trimmed;
};
